import {observer} from "mobx-react";
import {Typography, alpha} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import {Frame} from "../../common/Frame";
import {lessons} from "../../../proto/compiled";
import {textBlockStyleColor} from "../TextBlockStyles";

interface StyleSelectorProps {
  value: lessons.TextBlockStyle;
  onChange: (style: lessons.TextBlockStyle) => void;
}

export const StyleSelector = observer(({value, onChange}: StyleSelectorProps) => {
  const theme = useTheme();

  const options = [
    {
      value: lessons.TextBlockStyle.TEXT_STYLE_NORMAL,
      label: 'Normal',
      color: theme.palette.text.secondary,
      icon: <FormatQuoteIcon fontSize="small"/>,
    },
    {
      value: lessons.TextBlockStyle.TEXT_STYLE_GOOD_EMPHASIS,
      label: 'Good',
      color: textBlockStyleColor(lessons.TextBlockStyle.TEXT_STYLE_GOOD_EMPHASIS, theme)!,
      icon: <CheckIcon fontSize="small"/>,
    },
    {
      value: lessons.TextBlockStyle.TEXT_STYLE_BAD_EMPHASIS,
      label: 'Bad',
      color: textBlockStyleColor(lessons.TextBlockStyle.TEXT_STYLE_BAD_EMPHASIS, theme)!,
      icon: <CloseIcon fontSize="small"/>,
    },
    {
      value: lessons.TextBlockStyle.TEXT_STYLE_EMPHASIS,
      label: 'Note',
      color: textBlockStyleColor(lessons.TextBlockStyle.TEXT_STYLE_EMPHASIS, theme)!,
      icon: <InfoIcon fontSize="small"/>,
    },
    {
      value: lessons.TextBlockStyle.TEXT_STYLE_THEORY,
      label: 'Theory',
      color: textBlockStyleColor(lessons.TextBlockStyle.TEXT_STYLE_THEORY, theme)!,
      icon: <MenuBookIcon fontSize="small"/>,
    },
  ];

  return (
    <Frame direction="column" gap={0.5}>
      <Typography variant="body2" fontWeight={600}>
        Block Style
      </Typography>
      <Frame row gap={0.5} wrap>
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <Frame
              key={option.value}
              row
              gap={0.5}
              clickable
              paddingV={0.5}
              paddingH={1}
              radius={0.5}
              alignItems="center"
              style={{
                border: `1px solid ${isSelected ? option.color : theme.palette.divider}`,
                backgroundColor: isSelected ? alpha(option.color, 0.1) : 'transparent',
                transition: 'all 0.2s ease-in-out',
              }}
              onClick={() => onChange(option.value)}
            >
              <span style={{
                color: isSelected ? option.color : theme.palette.text.secondary,
                display: 'flex',
                alignItems: 'center',
              }}
              >
                {option.icon}
              </span>
              <Typography
                variant="caption"
                style={{
                  color: isSelected ? option.color : theme.palette.text.secondary,
                  fontWeight: isSelected ? 600 : 400,
                  userSelect: 'none',
                }}
              >
                {option.label}
              </Typography>
            </Frame>
          );
        })}
      </Frame>
    </Frame>
  );
});
